import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

export interface IConfigurationCardEntry {
  id: string;
  title: string;
  parentId?: string;
  /** Icona della card, mostrata nella barra di navigazione densa e nel suo menu. */
  icon?: React.ReactNode;
  /**
   * Testo da mostrare nella barra di navigazione densa. È sempre una stringa:
   * quando il `title` della card è un ReactNode (es. con Tag/contatori dinamici)
   * si usa l'`anchorTitle`, così la barra resta pulita e non "congela" un nodo
   * dinamico.
   */
  label?: string;
}

/**
 * Modalità di rendering condivisa da un `ConfigurationCardGroup` con tutte le
 * `ConfigurationCard` / `ConfigurationSubCard` figlie.
 */
export interface IConfigurationCardLayout {
  /**
   * Vista densa: layout a colonna singola (niente Anchor), header compatti e
   * `description` mostrata come popover ⓘ invece che a riga piena. Pensata per
   * i Drawer di configurazione dove lo spazio orizzontale è ridotto.
   */
  dense: boolean;
  /**
   * Rende le card collassabili: header cliccabile con freccia di espansione e body
   * collassabile. Le card partono aperte (vedi `defaultOpen` sulla card).
   */
  collapsible: boolean;
}

const DEFAULT_LAYOUT: IConfigurationCardLayout = {
  dense: false,
  collapsible: false,
};

export interface IConfigurationCardTreeEntry {
  id: string;
  title: string;
  children?: IConfigurationCardTreeEntry[];
}

type UpsertFn = (entry: IConfigurationCardEntry) => void;
type UnregisterFn = (entryId: string) => void;

const EntriesContext = createContext<IConfigurationCardEntry[]>([]);
const UpsertContext = createContext<UpsertFn | null>(null);
const UnregisterContext = createContext<UnregisterFn | null>(null);
const ParentCardIdContext = createContext<string | null>(null);
const LayoutContext = createContext<IConfigurationCardLayout>(DEFAULT_LAYOUT);
const FetchingContext = createContext<boolean>(false);

export const ConfigurationCardParentProvider = ParentCardIdContext.Provider;

function upsertEntry(entry: IConfigurationCardEntry) {
  return (prev: IConfigurationCardEntry[]) => {
    const index = prev.findIndex(e => e.id === entry.id);
    if (index === -1) return [...prev, entry];
    const updated = [...prev];
    updated[index] = entry;
    return updated;
  };
}

function removeEntry(entryId: string) {
  return (prev: IConfigurationCardEntry[]) =>
    prev.filter(e => e.id !== entryId);
}

export function ConfigurationCardContextProvider(props: {
  children: React.ReactNode;
  dense?: boolean;
  collapsible?: boolean;
  /** Propaga lo stato di refetch alle card, che mostrano un overlay Spin. */
  fetching?: boolean;
}) {
  const { children, dense = false, collapsible = false, fetching } = props;
  const [entries, setEntries] = useState<IConfigurationCardEntry[]>([]);

  const upsert = useCallback<UpsertFn>(entry => {
    setEntries(upsertEntry(entry));
  }, []);

  const unregister = useCallback<UnregisterFn>(entryId => {
    setEntries(removeEntry(entryId));
  }, []);

  const layout = useMemo<IConfigurationCardLayout>(
    () => ({ dense, collapsible }),
    [dense, collapsible],
  );

  return (
    <FetchingContext.Provider value={!!fetching}>
      <LayoutContext.Provider value={layout}>
        <UpsertContext.Provider value={upsert}>
          <UnregisterContext.Provider value={unregister}>
            <EntriesContext.Provider value={entries}>
              {children}
            </EntriesContext.Provider>
          </UnregisterContext.Provider>
        </UpsertContext.Provider>
      </LayoutContext.Provider>
    </FetchingContext.Provider>
  );
}

interface IRegisterOptions {
  isSubCard?: boolean;
  /** Icona della card, per la barra di navigazione densa. */
  icon?: React.ReactNode;
  /** Testo per la barra di navigazione densa (stringa, vs `title` per l'Anchor). */
  label?: string;
}

/**
 * Hook per registrare una ConfigurationCard nel context.
 * Restituisce l'id da applicare come attributo `id` sul DOM element.
 */
export function useConfigurationCardRegister(
  title: string,
  options?: IRegisterOptions,
): string {
  const upsert = useContext(UpsertContext);
  const unregister = useContext(UnregisterContext);
  const parentId = useContext(ParentCardIdContext);
  const reactId = useId();
  const id = `configuration-card-${reactId}`;

  const label = options?.label;

  // `icon` è un ReactNode: cambia identità a ogni render, quindi non può stare
  // nelle deps dell'effetto (creerebbe churn continuo). È statico nella pratica:
  // lo leggiamo dal ref al momento della registrazione. `label`, essendo ora una
  // stringa, sta invece direttamente nelle deps e resta sempre aggiornato (es.
  // se cambia il testo della sezione) senza congelarsi al valore del mount.
  const iconRef = useRef(options?.icon);
  iconRef.current = options?.icon;

  useEffect(() => {
    if (!upsert) return;
    upsert({
      id,
      title,
      icon: iconRef.current,
      label,
      parentId: options?.isSubCard ? (parentId ?? undefined) : undefined,
    });
  }, [upsert, id, title, label, parentId, options?.isSubCard]);

  useEffect(() => {
    return () => unregister?.(id);
  }, [unregister, id]);

  return id;
}

/**
 * Hook per ottenere la lista delle card registrate.
 */
export function useConfigurationCardEntries(): IConfigurationCardEntry[] {
  return useContext(EntriesContext);
}

/**
 * Hook per leggere la modalità di rendering (dense / collapsible) impostata dal
 * `ConfigurationCardGroup`. Fuori da un group restituisce i default (tutto
 * `false`), così le card usate da sole mantengono il comportamento storico.
 */
export function useConfigurationCardLayout(): IConfigurationCardLayout {
  return useContext(LayoutContext);
}

/**
 * Hook per sapere se il gruppo è in stato di refetch: le card lo usano per
 * mostrare un overlay Spin sul proprio contenuto mantenendolo visibile.
 */
export function useConfigurationCardFetching(): boolean {
  return useContext(FetchingContext);
}
